/* global ve */
import { createWebHashHistory, createRouter } from 'vue-router'
import { doLog, getUserProfile } from '@/js/veb.js'
import { displayToast } from '@/js/veinit.js'
import { i18n } from '@/main.js'

// Containers
const DefaultContainer = () => import('@/containers/DefaultContainerVE')

// Views
const dashboard = () => import('@/views/dashboard')
const orchestration = () => import('@/views/orchestration')
const microgrid = () => import('@/views/microgrid')
const setupwizard = () => import('@/views/setupwizard')
const customers = () => import('@/views/customers')
const customerservice = () => import('@/views/customerservice')
const admin = () => import('@/views/admin')
const assets = () => import('@/views/assets')
const events = () => import('@/views/events')
const analytics = () => import('@/views/analytics')
const financials = () => import('@/views/financials')
const contracts = () => import('@/views/contracts')
const install = () => import('@/views/install')
const logout = () => import('@/views/logout')
const userProfile = () => import('@/views/UserProfile')

// Views - Pages
const Page404 = () => import('@/views/pages/Page404')
const Page500 = () => import('@/views/pages/Page500')

const router = createRouter({
  // mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  scrollBehavior: () => ({ y: 0 }),
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      redirect: () => {
        const userProfile = getUserProfile()
        if (userProfile && userProfile.selectedVueView) {
          return { path: '/' + userProfile.selectedVueView }
        } else {
          return { path: '/customers' }
        }
      },
      name: 'Home',
      component: DefaultContainer,
      children: [
        {
          path: '/dashboard',
          name: 'Dashboard',
          meta: { requiresAuth: true },
          component: dashboard
        },
        {
          path: '/orchestration',
          name: 'Orchestration',
          meta: { requiresAuth: true },
          component: orchestration
        },
        {
          path: '/microgrid',
          name: 'Microgrid',
          meta: { requiresAuth: true },
          component: microgrid
        },
        {
          path: '/setupwizard',
          name: 'Setup Wizard',
          meta: { requiresAuth: true },
          component: setupwizard
        },
        {
          path: '/customers',
          name: 'Customers',
          meta: { requiresAuth: true },
          component: customers
        },
        {
          path: '/customerservice',
          name: 'CustomerService',
          meta: { requiresAuth: true },
          component: customerservice
        },
        {
          path: '/admin',
          name: 'Admin',
          meta: { requiresAuth: true },
          component: admin
        },
        {
          path: '/assets',
          name: 'Assets',
          meta: { requiresAuth: true },
          component: assets
        },
        {
          path: '/events',
          name: 'Events',
          meta: { requiresAuth: true },
          component: events
        },
        {
          path: '/analytics',
          name: 'Analytics',
          meta: { requiresAuth: true },
          component: analytics
        },
        {
          path: '/financials',
          name: 'Financial Accounts',
          meta: { requiresAuth: true },
          component: financials
        },
        {
          path: '/contracts',
          name: 'Contracts',
          meta: { requiresAuth: true },
          component: contracts
        },
        {
          path: '/install',
          name: 'Install',
          meta: { requiresAuth: true },
          component: install
        },

        {
          path: '/logout',
          name: 'Log Out',
          meta: { requiresAuth: true },
          component: logout
        },
        {
          path: '/user-profile',
          name: 'User Profile',
          meta: { requiresAuth: true },
          component: userProfile
        }
      ]
    },
    {
      path: '/pages',
      redirect: '/pages/404',
      name: 'Pages',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      children: [
        { path: '404', name: 'Page404', component: Page404 },
        { path: '500', name: 'Page500', component: Page500 }
      ]
    }
  ]
})

// default deny access nav items
const defaultDenyAccessTo = [
  'Orchestration',
  'Microgrid',
  'Customers',
  'CustomerService',
  'Admin',
  'Alerts',
  'Events',
  'Analytics',
  'Financial Accounts',
  'Financials',
  'Contracts',
  'Install'
]
const denyAccess = (toName, dbg = false) => {
  const fn = 'denyAccess'
  dbg = true
  let denyAccessTo = window.sessionStorage.getItem('denyAccessTo')
  doLog(fn, { toName, denyAccessTo, defaultDenyAccessTo }, dbg)
  denyAccessTo = typeof denyAccessTo === 'string' ? denyAccessTo.split(',') : defaultDenyAccessTo
  doLog(fn, { toName, denyAccessTo }, dbg)
  return denyAccessTo.includes(toName)
}

router.beforeEach(async (to, from, next) => {
  // 1. ADD THIS CHECK AT THE TOP:
  // If the route has no 'matched' components, it's not an internal
  // route (e.g., it's an external redirect to Cognito). Let it proceed.
  // const fn = 'routerBeforeEach'
  // doLog(fn, { to, from }, true)
  if (to.matched.length === 0) {
    next()
    return
  }
  // console.log('router.beforEach:from: ', from)
  // console.log('router.beforEach:to: ', to)
  const isAuthenticated = await ve.amplify.isAuthenticated() || false
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)
  // doLog(fn, { isAuthenticated, requiresAuth }, true)
  // console.log('router.beforeEach:requiresAuth: ' + requiresAuth)
  if (requiresAuth && !isAuthenticated) {
    // If authentication is required and user is not authenticated,
    // prevent navigation and let common.js handle the Amplify login redirect.
    next(false)
  } else if (!requiresAuth && isAuthenticated) {
    const userProfile = getUserProfile()
    if (userProfile && userProfile.selectedVueView) {
      next({ path: '/' + userProfile.selectedVueView })
    } else {
      next('/customers')
    }
  } else {
    if (denyAccess(to.name)) {
      displayToast(`${i18n.global.t(to.name)} ${i18n.global.t('Access Denied')}`)
      next(false)
    } else {
      next()
    }
  }
})

export default router
